import { EventIds } from 'constants/eventIds';
import { useCallback } from 'react';
import { fetchWithCommonHeaders } from '@landr/core';
import { Pack } from 'types/generated/samplesApi';
import { log } from 'utils/log';
import { CONFIG } from 'utils';

const ArtworkUrlGuidPlaceholder = 'GUID';

type PackSuggestionRequiredFields = Pick<
    Pack,
    'guid' | 'slug' | 'name' | 'artworkUrl'
>;

let packs: PackSuggestionRequiredFields[] = [];
let hasError = false;
let isLoading = false;
let loadPacksPromise: Promise<Response> | undefined = undefined;

export const usePacks = () => {
    const load = useCallback(async () => {
        if (loadPacksPromise) {
            return;
        }

        try {
            isLoading = true;
            loadPacksPromise = fetchWithCommonHeaders(
                `${CONFIG.VITE_API_URL}/packs/suggestions`,
                {
                    method: 'GET',
                    headers: {
                        accept: 'application/json',
                    },
                },
                {
                    log,
                    retryCount: 3,
                },
            );

            const response = await loadPacksPromise;
            const data: { suggestions: Pack[]; artworkUrl: string } =
                await response.json();

            const artworkUrlBase = data.artworkUrl;

            packs = data.suggestions.map((pack) => ({
                ...pack,
                artworkUrl: artworkUrlBase.replace(
                    ArtworkUrlGuidPlaceholder,
                    pack.guid,
                ),
            }));
        } catch (error) {
            log.error(
                'Failed to fetch packs',
                EventIds.PackSuggestionsError,
                error,
            );
            hasError = true;
        } finally {
            isLoading = false;
        }
    }, []);

    return {
        load,
        packs,
        hasError,
        isLoading,
    };
};
